import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { companyAtom } from '@/atoms/company';
import { reviewAnswerAtom } from '@/atoms/reviewAnswer';
import { userInstanceAtom } from '@/atoms/userDetails';
import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';
import i18n from '@/plugins/i18n';
import { getLocalizedText } from '@/utils/localization';
import { resolveManagerLabels } from '@/utils/managerLabel';

export const useCompanyInformation = () => {
    const company = useAtomValue(companyAtom);
    const reviewAnswer = useAtomValue(reviewAnswerAtom);
    const user = useAtomValue(userInstanceAtom);
    const { t } = useAmelioTranslations();

    const getJobTitle = useCallback(
        (jobTitleId: string): string => {
            const jobTitle = company?.jobTitles.find((jobTitle) => jobTitle.id === jobTitleId);
            return getLocalizedText(jobTitle?.names) || '';
        },
        [company?.jobTitles],
    );

    const getDepartment = useCallback(
        (departmentId: string): string => {
            const department = company?.departments.find(
                (department) => department.id === departmentId,
            );
            return getLocalizedText(department?.names) || '';
        },
        [company?.departments],
    );

    /**
     * The same resolution `@/plugins/managerLabelDefaults` feeds to i18next, read here for the
     * places that need the word in hand rather than inside a sentence — a capitalised label, or a
     * value handed to something that is not a translation.
     */
    const {
        managerLabel,
        managerPluralLabel,
        managerLabelCapitalized,
        managerPluralLabelCapitalized,
    } = resolveManagerLabels({
        language: i18n.language,
        defaultLanguage: company?.company.defaultLanguage,
        identifiers: company?.company.managerIdentifiers ?? reviewAnswer?.managerIdentifiers,
        pluralIdentifiers:
            company?.company.managerPluralIdentifiers ?? reviewAnswer?.managerPluralIdentifiers,
        userLabel: user?.managerLabel,
        userPluralLabel: user?.managerPluralLabel,
        translate: (key) => t(key),
    });

    const getManagerLabel = (): string => managerLabel;

    const getManagerPluralLabel = (): string => managerPluralLabel;

    /**
     * The same words, capitalised for the head of a standalone label — a column header, a tab, a
     * filter name. Engagement seeds the setting in lower case, which is what a sentence needs
     * ("votre gestionnaire") and what a label on its own does not. Never call these mid-sentence:
     * every interpolation inside a sentence takes the plain getter as it comes.
     */
    const getManagerLabelCapitalized = (): string => managerLabelCapitalized;

    const getManagerPluralLabelCapitalized = (): string => managerPluralLabelCapitalized;

    return {
        getJobTitle,
        getDepartment,
        getManagerLabel,
        getManagerLabelCapitalized,
        getManagerPluralLabel,
        getManagerPluralLabelCapitalized,
    };
};
